import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FlaskConical, Info, X } from 'lucide-react';

const PROVIDER_LABELS = {
  sleeper: 'Sleeper',
  yahoo: 'Yahoo',
};

const BETA_PROVIDERS = new Set(['sleeper', 'yahoo']);
const INFO_PROVIDERS = new Set(['espn']);

/**
 * Dismissible notice shown on league pages for providers that are not
 * yet fully supported (beta warning) or are being actively improved (soft info).
 * Only renders for providers in BETA_PROVIDERS or INFO_PROVIDERS.
 */
const ProviderBetaBanner = ({ provider, leagueId, leagueYear, leagueName }) => {
  const contactState = {
    provider,
    ...(leagueId && { league_id: leagueId }),
    ...(leagueYear && { league_year: leagueYear }),
    ...(leagueName && { league_name: leagueName }),
  };
  const [dismissed, setDismissed] = useState(false);

  const isBeta = BETA_PROVIDERS.has(provider);
  const isInfo = INFO_PROVIDERS.has(provider);

  if ((!isBeta && !isInfo) || dismissed) return null;

  if (isBeta) {
    const label = PROVIDER_LABELS[provider] ?? provider;
    return (
      <div className="mb-6 flex items-start gap-3 bg-mustard-light border border-mustard/40 border-l-[4px] border-l-mustard rounded-card px-4 py-3 shadow-card">
        <FlaskConical className="w-4 h-4 text-mustard shrink-0 mt-0.5" />
        <p className="flex-1 text-xs font-semibold text-text-mid leading-relaxed">
          <span className="font-extrabold text-ds-text">{label} support is in beta.</span>{' '}
          Some analytics may be limited or incorrect. If you notice anything unexpected,{' '}
          <Link
            to="/contact/"
            state={contactState}
            className="text-green font-bold hover:text-mustard transition-colors"
          >
            let us know
          </Link>
          .
        </p>
        <button
          onClick={() => setDismissed(true)}
          className="shrink-0 text-text-muted hover:text-ds-text transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="mb-6 flex items-start gap-3 bg-green-light border border-green-mid/30 border-l-[4px] border-l-green-mid rounded-card px-4 py-3 shadow-card">
      <Info className="w-4 h-4 text-green shrink-0 mt-0.5" />
      <p className="flex-1 text-xs font-semibold text-text-mid leading-relaxed">
        We&rsquo;re actively adding new features.{' '}
        If you spot anything off,{' '}
        <Link
          to="/contact/"
          state={contactState}
          className="text-green font-bold hover:text-forest transition-colors"
        >
          let us know
        </Link>
        .
      </p>
      <button
        onClick={() => setDismissed(true)}
        className="shrink-0 text-text-muted hover:text-ds-text transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default ProviderBetaBanner;
